import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, HelpCircle, Info } from 'lucide-react';

const QuestionSlider = ({ 
  question, 
  value, 
  onChange,
  className = "" 
}) => {
  const [animation, setAnimation] = useState(false);
  const [currentValue, setCurrentValue] = useState(value !== undefined ? value : null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveringLabel, setHoveringLabel] = useState(null);
  const sliderRef = useRef(null);
  const detailsRef = useRef(null);
  
  // Ensure we have the necessary question properties or set defaults
  const {
    id,
    text,
    subtext,
    min = 1,
    max = 4,
    labels = [],
    details = [],
    valueMap = null,
    riskLevel = 'medium', // 'low', 'medium', 'high'
    helpText
  } = question;

  // Update local state when prop value changes
  useEffect(() => {
    if (value !== undefined) {
      setCurrentValue(value);
    }
  }, [value]);

  // Handle animation duration
  useEffect(() => {
    if (animation) {
      const timer = setTimeout(() => setAnimation(false), 300);
      return () => clearTimeout(timer);
    }
  }, [animation]);

  // Add keyboard navigation accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!sliderRef.current || document.activeElement !== sliderRef.current) return;
      
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        handleChange({ target: { value: Math.min(currentValue !== null ? currentValue + 0.5 : min, max) } });
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleChange({ target: { value: Math.max(currentValue !== null ? currentValue - 0.5 : min, min) } });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentValue, min, max]);

  // Handle details panel transitions
  useEffect(() => {
    if (detailsRef.current) {
      detailsRef.current.style.height = "0";
      detailsRef.current.style.opacity = "0";
      
      setTimeout(() => {
        const height = detailsRef.current?.scrollHeight || 0;
        if (detailsRef.current) {
          detailsRef.current.style.height = `${height}px`;
          detailsRef.current.style.opacity = "1";
        }
      }, 50);
    }
  }, [currentValue]);

  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    
    // Ensure we're setting a valid numeric value
    if (!isNaN(newValue)) {
      setCurrentValue(newValue);
      
      // Map the value if needed
      const actualValue = valueMap ? valueMap[newValue] : newValue;
      
      // Only trigger onChange with valid values
      if (actualValue !== undefined) {
        onChange(id, actualValue);
      }
      
      // Trigger animation
      setAnimation(true);
    }
  };

  const handleSliderHover = () => {
    setShowTooltip(true);
  };

  const handleSliderLeave = () => {
    setShowTooltip(false);
  };

  const handleLabelHover = (value) => {
    setHoveringLabel(value);
  };

  const handleLabelLeave = () => {
    setHoveringLabel(null);
  };

  // Handle explicit label click
  const handleLabelClick = (value) => {
    handleChange({ target: { value } });
  };

  // Determine theme colors based on risk level - Dark theme versions
  const getThemeColor = () => {
    switch (riskLevel) {
      case 'low': return 'bg-emerald-500 text-emerald-100 border-emerald-400';
      case 'high': return 'bg-rose-500 text-rose-100 border-rose-400';
      default: return 'bg-arc-gold text-blue-100 border-blue-400';
    }
  };

  const getThemeContainerClass = () => {
    switch (riskLevel) {
      case 'low': return 'bg-arc-elevated border-emerald-500/30';
      case 'high': return 'bg-arc-elevated border-rose-500/30';
      default: return 'bg-arc-elevated border-blue-500/30';
    }
  };

  const getTrackBackground = () => {
    if (currentValue === null) return 'bg-white/10';
    
    // Create gradient for track - Dark theme colors
    const percentage = ((currentValue - min) / (max - min)) * 100;
    const themeColor = riskLevel === 'low' ? '#10b981' : riskLevel === 'high' ? '#f43f5e' : '#3b82f6';
    
    return `linear-gradient(to right, ${themeColor} 0%, ${themeColor} ${percentage}%, #475569 ${percentage}%, #475569 100%)`;
  };

  // Generate all possible values (including half points)
  const getAllValues = () => {
    const values = [];
    for (let i = min; i <= max; i += 0.5) {
      values.push(i);
    }
    return values;
  };

  // Calculate the positions for labels (only show whole number labels)
  const getMainLabelPositions = () => {
    const wholeNumbers = [];
    for (let i = min; i <= max; i += 1) {
      wholeNumbers.push(i);
    }
    return wholeNumbers;
  };

  // Calculate position percentage for a given value
  const getValuePosition = (value) => {
    const totalRange = max - min;
    const position = ((value - min) / totalRange) * 100;
    return `${position}%`;
  };

  // Format label based on position - Dark theme
  const getLabelClass = (value) => {
    const isActive = currentValue === value;
    
    return `absolute text-sm font-medium transition-all duration-300 ease-in-out cursor-pointer ${
      isActive ? 'text-arc-ink font-semibold' : 'text-arc-muted hover:text-arc-ink'
    }`;
  };

  // Get label text for a given value
  const getLabelText = (value) => {
    const valueIndex = Math.round((value - min) * 2); // Convert to index for half-steps
    if (labels.length > valueIndex) {
      return labels[valueIndex];
    }
    return value.toString();
  };

  // Get details text for a given value
  const getDetailsText = (value) => {
    const valueIndex = Math.round((value - min) * 2); // Convert to index for half-steps
    if (details.length > valueIndex) {
      return details[valueIndex];
    }
    return null;
  };

  // Generate tick marks for the slider (including half points) - Dark theme
  const allValues = getAllValues();
  const tickMarks = allValues.map((value, idx) => {
    const isActive = currentValue !== null && currentValue >= value;
    const isWholeNumber = value % 1 === 0;
    const colorClass = isActive ? getThemeColor().split(' ')[0] : 'bg-white/15';
    const height = isWholeNumber ? 'h-4' : 'h-2'; // Taller ticks for whole numbers
    
    return (
      <div
        key={idx}
        className={`absolute w-1 ${height} rounded-full ${colorClass} transition-colors duration-300 border border-slate-600`}
        style={{ 
          left: getValuePosition(value), 
          transform: 'translateX(-50%)',
          top: isWholeNumber ? '-2px' : '2px'
        }}
        aria-hidden="true"
      />
    );
  });

  // Get main labels (whole numbers only)
  const mainLabels = getMainLabelPositions();

  return (
    <div className={`p-6 rounded-xl mb-6 transition-all duration-300 ease-in-out border bg-arc-elevated border-slate-600 min-h-[400px] flex flex-col ${animation ? 'scale-102 shadow-lg shadow-slate-900/50' : 'scale-100'} ${className}`}>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-xl font-semibold text-arc-ink mb-1">{text}</h3>
          {subtext && <p className="text-arc-muted text-sm">{subtext}</p>}
        </div>
        
        {helpText && (
          <div className="relative">
            <button
              type="button"
              className="text-arc-muted hover:text-arc-ink focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-slate-700 rounded-full transition-colors"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              aria-label="Help information"
            >
              <HelpCircle size={20} />
            </button>
            
            {showTooltip && (
              <div className="absolute z-10 right-0 mt-2 w-64 p-3 bg-arc-surface rounded-lg shadow-xl border border-slate-600 text-sm text-arc-ink">
                <div className="flex items-start">
                  <Info size={16} className="text-arc-gold mr-2 mt-0.5 flex-shrink-0" />
                  <p>{helpText}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Slider control */}
      <div className="flex-1 flex flex-col justify-center">
        <div className="relative pt-6 pb-12">
          {/* Tick marks container */}
          <div className="absolute w-full h-6 flex items-center" aria-hidden="true">
            {tickMarks}
          </div>
          
          <input
            ref={sliderRef}
            type="range"
            min={min}
            max={max}
            step="0.05"
            value={currentValue !== null ? currentValue : min}
            onChange={handleChange}
            onMouseEnter={handleSliderHover}
            onMouseLeave={handleSliderLeave}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-700"
            style={{ 
              background: getTrackBackground(),
              WebkitAppearance: 'none', 
              appearance: 'none',
              zIndex: 10,
              position: 'relative'
            }}
            aria-labelledby={`slider-label-${id}`}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={currentValue !== null ? currentValue : min}
            aria-valuetext={currentValue !== null ? getLabelText(currentValue) : ''}
          />
          
          {/* Labels - only show for whole numbers */}
          <div className="relative w-full mt-8">
            {mainLabels.map((value) => (
              <div 
                key={value}
                className={getLabelClass(value)}
                style={{ 
                  left: getValuePosition(value),
                  transform: value === min ? 'translateX(0)' : value === max ? 'translateX(-100%)' : 'translateX(-50%)',
                  fontWeight: currentValue === value ? 600 : 400
                }}
                onMouseEnter={() => handleLabelHover(value)}
                onMouseLeave={handleLabelLeave}
                onClick={() => handleLabelClick(value)}
              >
                {getLabelText(value)}
                
                {/* Label hover effect */}
                {hoveringLabel === value && getDetailsText(value) && (
                  <div className="absolute z-20 bottom-full mb-2 p-2 bg-arc-surface rounded shadow-xl text-xs w-48 border border-slate-600 text-arc-ink">
                    {getDetailsText(value)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Fixed height details section */}
      <div className="h-24 mt-4">
        {currentValue !== null && getDetailsText(currentValue) ? (
          <div className={`p-4 rounded-lg border ${getThemeContainerClass()} h-full flex items-start`}>
            <AlertCircle 
              size={18} 
              className={`${riskLevel === 'low' ? 'text-emerald-400' : riskLevel === 'high' ? 'text-rose-400' : 'text-arc-gold'} flex-shrink-0`}
              style={{ marginTop: '2px', marginRight: '8px' }} 
            />
            <div className="flex-1 overflow-hidden">
              <p className="font-medium mb-1 text-arc-ink">
                {getLabelText(currentValue)}
              </p>
              <p className="text-arc-muted text-sm leading-relaxed overflow-y-auto max-h-12">
                {getDetailsText(currentValue)}
              </p>
            </div>
          </div>
        ) : currentValue !== null ? (
          <div className="h-full flex items-center justify-center">
            <span className={`inline-block px-4 py-2 rounded-full font-medium bg-white/10 border border-slate-500 text-arc-ink`}>
              Selected: <strong className="text-arc-ink">{getLabelText(currentValue)}</strong>
            </span>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <span className="text-arc-muted text-sm">Move the slider to see details</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionSlider;